import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import VideoFilters from "@/components/feature/VideoFilters";
import EmptyState from "@/components/base/EmptyState";
import { categories, videos } from "@/mocks/videos";
import {
  countByCategory,
  getCategory,
  getCategoryVideos,
  type DurationKey,
  type SortKey,
} from "@/lib/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const navigate = useNavigate();
  const category = getCategory(categoryId);
  const categoryName = category?.name ?? "分类";

  const [sort, setSort] = useState<SortKey>("latest");
  const [duration, setDuration] = useState<DurationKey>("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: `${categoryName} - 茄子视频短视频`,
    description:
      category?.description ??
      "茄子视频分类视频列表，按最新与最热排序，支持时长筛选，快速找到想看的短视频。",
    keywords: `茄子视频,${categoryName}视频,短视频,${categoryName}短视频,在线观看`,
  });

  const list = useMemo(
    () => (category ? getCategoryVideos(category.id, sort, duration) : []),
    [category, sort, duration]
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [categoryId, sort, duration]);

  const resetFilters = () => {
    setSort("latest");
    setDuration("all");
  };

  const openRandom = () => {
    const pool = category ? getCategoryVideos(category.id) : videos;
    if (pool.length === 0) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    navigate(`/video/${pick.id}`);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1360px] px-4 pt-6 md:px-8 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: "/category/entertainment" },
            { label: categoryName },
          ]}
        />

        <div className="mt-5 rounded-2xl bg-primary-50 px-5 py-7 md:px-8 md:py-9">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className={`${category?.icon ?? "ri-folder-line"} text-lg`}></i>
                </span>
                <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
                  Category
                </p>
              </div>
              <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-[32px]">
                {categoryName}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {category?.description ?? "这个分类暂时还没有内容，去看看其他分类吧。"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-background-50 px-3 py-1.5 font-label text-xs text-foreground-600">
                {countByCategory(categoryId)} 条内容
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1.5 font-label text-xs text-accent-900">
                每日更新
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 flex w-full max-w-[1360px] flex-col gap-8 px-4 pb-4 md:px-8 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-[248px]">
          <div className="lg:sticky lg:top-24">
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="px-2 pb-2 font-label text-[11px] uppercase tracking-[0.16em] text-foreground-500">
                全部类别
              </p>
              <div className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                {categories.map((item) => {
                  const active = item.id === categoryId;
                  return (
                    <Link
                      key={item.id}
                      to={`/category/${item.id}`}
                      className={`flex shrink-0 cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
                        active
                          ? "bg-primary-500 font-medium text-background-50"
                          : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                      }`}
                    >
                      <i className={`${item.icon} text-base`}></i>
                      <span className="whitespace-nowrap">{item.name}</span>
                      <span
                        className={`ml-auto hidden font-label text-[11px] lg:inline ${
                          active ? "text-background-200" : "text-foreground-400"
                        }`}
                      >
                        {countByCategory(item.id)}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 hidden rounded-lg bg-secondary-100 p-5 lg:block">
              <i className="ri-shuffle-line text-2xl text-secondary-700"></i>
              <h2 className="mt-3 font-heading text-base font-bold text-secondary-950">不知道看什么？</h2>
              <p className="mt-1.5 text-xs leading-relaxed text-secondary-800">
                让系统从当前分类里随机挑一条，反正只有几分钟。
              </p>
              <button
                type="button"
                onClick={openRandom}
                disabled={!category}
                className="mt-4 flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-800 px-3 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-play-fill"></i>
                随机播放一条
              </button>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <VideoFilters
            sort={sort}
            duration={duration}
            onSortChange={setSort}
            onDurationChange={setDuration}
            onReset={resetFilters}
            resultCount={list.length}
          />

          {list.length === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon="ri-file-search-line"
                title="暂无符合条件视频"
                description="当前分类下没有满足筛选条件的视频，换个时长条件或者重置筛选试试。"
              >
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  返回首页
                </Link>
              </EmptyState>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 xl:grid-cols-3">
                {list.slice(0, visible).map((video) => (
                  <VideoCard key={video.id} video={video} showCategory={false} />
                ))}
              </div>

              <div className="mt-10 flex flex-col items-center gap-3">
                {visible < list.length ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setVisible((value) => value + PAGE_SIZE)}
                      className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      <i className="ri-add-line"></i>
                      加载更多视频
                    </button>
                    <p className="font-label text-xs text-foreground-400">
                      已显示 {Math.min(visible, list.length)} / {list.length} 条
                    </p>
                  </>
                ) : (
                  <p className="font-label text-xs tracking-wide text-foreground-400">
                    已经到底啦 · 共 {list.length} 条视频
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}